import { gql } from '@apollo/client';

export const UPDATE_FIELDS_CONFIG = gql`
    mutation UpdateDocumentConfig(
        $channelId: UUID!
        $documentType: String!
        $documentConfig: DocumentConfigInput
        $fieldConfigs: [FieldConfigInput]
    ) {
        updateDocumentConfig(
            channelId: $channelId
            documentType: $documentType
            documentConfig: $documentConfig
            fieldConfigs: $fieldConfigs
        ) {
            success
        }
    }
`;

export const MOVE_DOCUMENTS_BETWEEN_CHANNELS = gql`
    mutation (
        $documentIds: [UUID!]!
        $sourceDocumentType: DocumentTypeEnum!
        $targetDocumentType: DocumentTypeEnum!
        $targetChannelId: UUID!
    ) {
        moveDocumentsBetweenChannels(
            documentIds: $documentIds
            sourceDocumentType: $sourceDocumentType
            targetDocumentType: $targetDocumentType
            targetChannelId: $targetChannelId
        ) {
            success
        }
    }
`;

export const REPROCESS_DOCUMENT = gql`
    mutation ($documentType: DocumentTypeEnum!, $recordId: UUID!) {
        reprocessDocument(documentType: $documentType, recordId: $recordId) {
            success
        }
    }
`;

export const DISCARD_DOCUMENTS = gql`
    mutation ($recordIds: [UUID!]!, $documentType: DocumentTypeEnum!, $reason: String) {
        discardDocuments(recordIds: $recordIds, documentType: $documentType, reason: $reason) {
            success
        }
    }
`;

export const DELETE_DOCUMENTS = gql`
    mutation ($recordIds: [UUID!]!, $documentType: DocumentTypeEnum!, $reason: String) {
        deleteDocuments(recordIds: $recordIds, documentType: $documentType, reason: $reason) {
            success
        }
    }
`;

export const GET_DOCUMENT_CLIENT_TYPE_TABLE_FILTER_OPTIONS = gql`
    query ($documentType: DocumentTypeEnum!, $searchTerm: String) {
        documentClientTypeTableFilterOptions(documentType: $documentType, searchTerm: $searchTerm) {
            label
            value
            count
        }
    }
`;

export const GET_DOCUMENT_CLIENT_TABLE_FILTER_OPTIONS = gql`
    query ($documentType: DocumentTypeEnum!, $searchTerm: String) {
        documentClientTableFilterOptions(documentType: $documentType, searchTerm: $searchTerm) {
            label
            value
            count
        }
    }
`;

export const SWAP_SOURCE_DATA = gql`
    mutation swapSourceData($documentType: DocumentTypeEnum!, $recordId: UUID!, $targetFile: EmailAttachmentInput!) {
        swapSourceData(documentType: $documentType, recordId: $recordId, targetFile: $targetFile) {
            success
        }
    }
`;

export const UPDATE_SOURCE_PART_CLASSIFICATION = gql`
    mutation UpdateSourcePartClassification($sourcePartId: UUID!, $classificationLabel: String!) {
        updateSourcePartClassification(sourcePartId: $sourcePartId, classificationLabel: $classificationLabel) {
            success
        }
    }
`;

export const CREATE_RECORD_FROM_EMAIL_PART = gql`
    mutation CreateRecordFromEmailPart(
        $documentType: DocumentTypeEnum!
        $recordId: UUID!
        $emailPartId: UUID!
        $deleteSourceRecord: Boolean
        $deleteReason: String
    ) {
        createRecordFromEmailPart(
            documentType: $documentType
            recordId: $recordId
            emailPartId: $emailPartId
            deleteSourceRecord: $deleteSourceRecord
            deleteReason: $deleteReason
        ) {
            success
            recordId
        }
    }
`;

// LLM Extractor Config Fragments
export const LLM_EXTRACTOR_INPUT_FRAGMENT = gql`
    fragment LLMExtractorInputFragment on LLMExtractorInputType {
        name
        applicableForLineItems
        applicableForHeader
        isConfiguredForChannel
        fieldPath
    }
`;

export const LLM_CONTEXT_FILE_FRAGMENT = gql`
    fragment LLMContextFileFragment on LLMContextFileType {
        id
        name
        size
        tokenCount
        url
    }
`;

export const LLM_EXTRACTOR_CONFIG_FRAGMENT = gql`
    ${LLM_CONTEXT_FILE_FRAGMENT}
    fragment LLMExtractorConfigFragment on LLMExtractorConfigType {
        id
        fields
        model
        reasoningEffort
        lineItemOperationsMode
        useRag
        prompt
        documentInput
        tools
        contextFiles {
            ...LLMContextFileFragment
        }
        isEnabled
        testingOnly
        name
        isDefault
        partitionIds
    }
`;

export const GET_AVAILABLE_LLM_MODELS = gql`
    query {
        availableLlmModels {
            value
            label
            isReasoning
        }
    }
`;

export const GET_LLM_EXTRACTOR_INPUT_OPTIONS = gql`
    ${LLM_EXTRACTOR_INPUT_FRAGMENT}
    query LlmExtractorInputOptions($documentType: DocumentTypeEnum!, $channelId: UUID!) {
        llmExtractorInput(documentType: $documentType, channelId: $channelId) {
            ...LLMExtractorInputFragment
        }
    }
`;

export const GET_LLM_EXTRACTOR_OUTPUT_OPTIONS = gql`
    query LlmExtractorOutputOptions($documentType: DocumentTypeEnum!, $channelId: UUID!) {
        llmExtractorOutput(documentType: $documentType, channelId: $channelId) {
            name
            applicableForLineItems
            applicableForHeader
            isConfiguredForChannel
        }
    }
`;

export const GET_LLM_EXTRACTOR_TOOL_OPTIONS = gql`
    query {
        llmExtractorTools {
            name
            description
        }
    }
`;

export const GET_HEADER_LLM_EXTRACTOR_CONFIG = gql`
    ${LLM_EXTRACTOR_CONFIG_FRAGMENT}
    query ($documentType: DocumentTypeEnum!, $channelId: UUID!) {
        rules: headerLlmExtractorConfigs(documentType: $documentType, channelId: $channelId) {
            ...LLMExtractorConfigFragment
        }
    }
`;

export const GET_LINE_ITEM_LLM_EXTRACTOR_CONFIG = gql`
    ${LLM_EXTRACTOR_CONFIG_FRAGMENT}
    query ($documentType: DocumentTypeEnum!, $channelId: UUID!) {
        rules: lineItemLlmExtractorConfigs(documentType: $documentType, channelId: $channelId) {
            ...LLMExtractorConfigFragment
        }
    }
`;

export const GET_LLM_EXTRACTOR_CONFIG_APPLY_PREVIEW = gql`
    ${LLM_EXTRACTOR_CONFIG_FRAGMENT}
    query LlmExtractorConfigApplyPreview($documentType: DocumentTypeEnum!, $channelId: UUID!, $configId: UUID!) {
        targets: llmExtractorConfigApplyPreview(
            documentType: $documentType
            channelId: $channelId
            configId: $configId
        ) {
            channelId
            channelName
            outcome
            config {
                ...LLMExtractorConfigFragment
            }
            takenClient
            isRecommended
            leftOutFields
            leftOutInputs
            filesToAdd
            filesToRemove
            groupKey
        }
    }
`;

export const APPLY_LLM_EXTRACTOR_CONFIG = gql`
    mutation ApplyLlmExtractorConfig(
        $documentType: DocumentTypeEnum!
        $channelId: UUID!
        $configId: UUID!
        $targetChannelIds: [UUID!]!
    ) {
        applyLlmExtractorConfig(
            documentType: $documentType
            channelId: $channelId
            configId: $configId
            targetChannelIds: $targetChannelIds
        ) {
            results {
                channelId
                success
            }
        }
    }
`;

// LLM Extractor Config Mutations
export const CREATE_HEADER_LLM_EXTRACTOR_CONFIG = gql`
    mutation ($documentType: DocumentTypeEnum!, $channelId: UUID!, $isDefault: Boolean) {
        created: createHeaderLlmExtractorConfig(
            documentType: $documentType
            channelId: $channelId
            isDefault: $isDefault
        ) {
            llmExtractorConfig {
                id
            }
        }
    }
`;

export const CREATE_LINE_ITEM_LLM_EXTRACTOR_CONFIG = gql`
    mutation ($documentType: DocumentTypeEnum!, $channelId: UUID!, $isDefault: Boolean) {
        created: createLineItemLlmExtractorConfig(
            documentType: $documentType
            channelId: $channelId
            isDefault: $isDefault
        ) {
            llmExtractorConfig {
                id
            }
        }
    }
`;

export const UPDATE_LLM_EXTRACTOR_CONFIG = gql`
    mutation (
        $id: UUID!
        $fields: [String]
        $model: String
        $reasoningEffort: String
        $lineItemOperationsMode: Boolean
        $useRag: Boolean
        $prompt: String
        $documentInput: [String]
        $tools: [String]
        $isEnabled: Boolean
        $testingOnly: Boolean
        $name: String
        $isDefault: Boolean
        $partitionIds: [String]
    ) {
        updateLlmExtractorConfig(
            id: $id
            fields: $fields
            model: $model
            reasoningEffort: $reasoningEffort
            lineItemOperationsMode: $lineItemOperationsMode
            useRag: $useRag
            prompt: $prompt
            documentInput: $documentInput
            tools: $tools
            isEnabled: $isEnabled
            testingOnly: $testingOnly
            name: $name
            isDefault: $isDefault
            partitionIds: $partitionIds
        ) {
            llmExtractorConfig {
                id
            }
        }
    }
`;

export const UPLOAD_LLM_CONTEXT_FILE = gql`
    mutation ($input: LLMContextFileUploadInput!) {
        uploadLlmContextFile(input: $input) {
            success
        }
    }
`;

export const GET_CUSTOMIZATION_PARAMS = gql`
    query GetCustomizationParams($channelId: UUID!) {
        customizationParams(channelId: $channelId) {
            channelId
            params
            schema
        }
    }
`;

export const UPDATE_CUSTOMIZATION_PARAMS = gql`
    mutation UpdateCustomizationParams($channelId: UUID!, $params: JSONString!) {
        updateCustomizationParams(channelId: $channelId, params: $params) {
            success
            errors {
                path
                message
            }
            customizationParams {
                channelId
                params
                schema
            }
        }
    }
`;

export const DELETE_LLM_CONTEXT_FILE = gql`
    mutation ($id: UUID!) {
        deleteLlmContextFile(id: $id) {
            success
        }
    }
`;

export const DELETE_LLM_EXTRACTOR_CONFIG = gql`
    mutation ($id: UUID!) {
        deleteLlmExtractorConfig(id: $id) {
            success
        }
    }
`;

export const BASE_FIELD = `
    bbox
    pageIndex
    isEmailOcr
    predictionConfidence
    confidenceExplanation {
        translationKey
        explanationDetails
    }
    validationChecks {
        name
        result
        targetValue
        isBlocking
        explanationDetails
    }
`;

export const BASE_DOCUMENT = `
    clientPartitionFieldName
    validationChecks {
            name
            result
            targetValue
            reference
            explanationDetails
        }
`;

export const PRIMITIVE_FIELD = `
    ${BASE_FIELD}
    value
`;

export const EXTRACTION_SEARCH_RESULTS = `
    extractionSearchResults {
        timestamp
        results {
            articleId
            description
        }
    }
`;

export const SELECT_FIELD_FRAGMENT = gql`
    fragment FullSelectField on SelectField {
        ${PRIMITIVE_FIELD}
        choices
        options {
            value
            label {
                locale
                string
            }
            description {
                locale
                string
            }
        }
    }
`;

export const ACQUIRE_LOCK = gql`
    mutation ($groupName: String!, $identifier: UUID!, $userId: String!) {
        acquireLock(groupName: $groupName, identifier: $identifier, userId: $userId) {
            isLocked
            lockedAt
            lockedBy {
                id
                firstName
                lastName
                email
            }
            success
        }
    }
`;

export const RELEASE_LOCK = gql`
    mutation ($groupName: String!, $identifier: UUID!, $userId: String!) {
        releaseLock(groupName: $groupName, identifier: $identifier, userId: $userId) {
            success
        }
    }
`;

export const GET_MATCHING_CONFIG = gql`
    query MatchingConfig($documentType: DocumentTypeEnum!, $channelId: UUID!) {
        matchingConfig(documentType: $documentType, channelId: $channelId) {
            id
            isPersisted
            isMatchingEnabled
            sharedChannelNames
            fields {
                name
                isLineItem
                isEnabledForChannel
                applyMatching
                blockAutomation
                comparisonType
                thresholdValue
                upperThresholdValue
                maxAllowedDeviation
                cleaningMethods
            }
            supportedFields {
                name
                isLineItem
                isEnabledForChannel
                defaultComparisonType
                allowedComparisonTypes
                valueType
            }
            documentNumberField
        }
    }
`;

export const UPDATE_MATCHING_CONFIG = gql`
    mutation UpdateMatchingConfig(
        $documentType: DocumentTypeEnum!
        $channelId: UUID!
        $fields: [MatchingFieldConfigInput!]!
        $applyToAllChannels: Boolean
    ) {
        updateMatchingConfig(
            documentType: $documentType
            channelId: $channelId
            fields: $fields
            applyToAllChannels: $applyToAllChannels
        ) {
            matchingConfig {
                id
                isPersisted
            }
        }
    }
`;

export const GET_FIELD_ASSISTANCE_STATS = gql`
    query FieldAssistanceStats(
        $documentType: DocumentTypeEnum!
        $channelId: UUID!
        $startDate: Date!
        $endDate: Date!
    ) {
        fieldAssistanceStats(
            documentType: $documentType
            channelId: $channelId
            startDate: $startDate
            endDate: $endDate
        ) {
            documentCount
            fields {
                group
                fieldName
                documentCount
                reasons {
                    reason
                    documentCount
                }
            }
        }
    }
`;

export const GET_FIELD_ASSISTANCE_EXAMPLES = gql`
    query FieldAssistanceExamples(
        $documentType: DocumentTypeEnum!
        $channelId: UUID!
        $startDate: Date!
        $endDate: Date!
        $group: String!
        $fieldName: String!
        $reason: String!
        $offset: Int
        $first: Int
    ) {
        fieldAssistanceExamples(
            documentType: $documentType
            channelId: $channelId
            startDate: $startDate
            endDate: $endDate
            group: $group
            fieldName: $fieldName
            reason: $reason
            offset: $offset
            first: $first
        ) {
            recordId
            finishedAt
            isCorrected
            documentNumber
            clientName
            clientNumber
            values {
                subField
                lineItemIndex
                readValue
                correctedValue
            }
        }
    }
`;
